import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { setCredentials } from "../features/auth/authSlice";
import {
  useRegisterMutation,
  useVerifyMutation,
  useLoginMutation,
} from "../api/apiSlice";
import logo from "../img/logo.png";

// Inline официальный Google G — раньше тянули с svgrepo.com через <img>, и
// на iOS Safari часть запросов отваливалась (ITP / network glitch). Inline
// SVG не зависит от сети и от CSP, всегда отрисуется.
const GoogleIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8a12 12 0 1 1 0-24c3 0 5.7 1 7.8 3l5.7-5.7A20 20 0 1 0 24 44a20 20 0 0 0 19.6-23.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8A12 12 0 0 1 24 12c3 0 5.7 1 7.8 3l5.7-5.7A20 20 0 0 0 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44a20 20 0 0 0 13.4-5.2l-6.2-5.2A12 12 0 0 1 12.7 28l-6.6 5A20 20 0 0 0 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.6-.4-3.5z"/>
  </svg>
);

export default function AuthPage() {
  const [step, setStep] = useState("register"); // register | verify | login
  const [form, setForm] = useState({ name: "", email: "", password: "", code: "" });
  const [msg, setMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [register, { isLoading: isRegistering }] = useRegisterMutation();
  const [verify, { isLoading: isVerifying }] = useVerifyMutation();
  const [login, { isLoading: isLoggingIn }] = useLoginMutation();

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const doRegister = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      await register({ name: form.name, email: form.email, password: form.password }).unwrap();
      setMsg("📩 Код отправлен на вашу почту. Проверьте папку «Входящие» и спам.");
      setStep("verify");
    } catch (err) {
      setMsg(err.data?.message || "❌ Ошибка регистрации. Попробуйте еще раз.");
    }
  };

  const doVerify = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      const response = await verify({ email: form.email, code: form.code }).unwrap();
      dispatch(setCredentials(response));
      localStorage.setItem("refreshToken", response.refreshToken);
      setMsg("✅ Аккаунт успешно подтверждён. Выполняется вход...");
      navigate("/dashboard/student");
    } catch (err) {
      setMsg(err.data?.message || "❌ Неверный код. Попробуйте снова.");
    }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      const response = await login({ email: form.email, password: form.password }).unwrap();
      dispatch(setCredentials(response));
      localStorage.setItem("refreshToken", response.refreshToken);
      setMsg("🎉 Успешный вход! Перенаправление...");
      navigate("/dashboard/student"); // Немесе рөлге байланысты басқа бетке
    } catch (err) {
      setMsg(err.data?.message || "❌ Ошибка входа. Проверьте email и пароль.");
    }
  };

  const doGoogle = () => {
    const googleAuthUrl = `${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/auth/google`;
    window.location.href = googleAuthUrl;
  };

  const renderForm = () => {
    switch (step) {
      case "register":
        return (
          <form onSubmit={doRegister} className="space-y-6">
            <h2 className="text-2xl font-extrabold text-gray-900 text-center">Создать аккаунт</h2>
            <input name="name" placeholder="Имя" onChange={onChange} required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500" />
            <input name="email" type="email" placeholder="Email" onChange={onChange} required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500" />
            <div className="relative">
              <input name="password" type={showPassword ? "text" : "password"} placeholder="Пароль" onChange={onChange} required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 pr-12" />
              <button type="button" className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
              </button>
            </div>
            <button type="submit" className="w-full bg-blue-600 text-white font-semibold py-3 rounded-xl shadow-md hover:bg-blue-700 transition disabled:opacity-50" disabled={isRegistering}>
              {isRegistering ? "Регистрация..." : "Зарегистрироваться"}
            </button>
            <button type="button" onClick={doGoogle} className="w-full bg-white border border-gray-300 text-gray-700 py-3 rounded-xl shadow-sm hover:bg-gray-50 transition flex items-center justify-center gap-3 font-medium">
              <GoogleIcon className="w-5 h-5" />
              Продолжить через Google
            </button>
          </form>
        );
      case "verify":
        return (
          <form onSubmit={doVerify} className="space-y-6">
            <h2 className="text-2xl font-extrabold text-gray-900 text-center">Подтверждение</h2>
            <p className="text-sm text-gray-600 text-center">Мы отправили код на <span className="font-medium">{form.email}</span></p>
            <input name="code" placeholder="Код из письма" onChange={onChange} required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500" />
            <button type="submit" className="w-full bg-green-600 text-white font-semibold py-3 rounded-xl shadow-md hover:bg-green-700 transition disabled:opacity-50" disabled={isVerifying}>
              {isVerifying ? "Проверка..." : "Подтвердить"}
            </button>
          </form>
        );
      case "login":
        return (
          <form onSubmit={doLogin} className="space-y-6">
             <h2 className="text-2xl font-extrabold text-gray-900 text-center">Вход</h2>
             <p className="text-center text-sm">Нет аккаунта? <button type="button" onClick={() => setStep('register')} className="font-medium text-blue-600 hover:underline">Регистрация</button></p>
             <input name="email" type="email" placeholder="Email" onChange={onChange} required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500" />
            <div className="relative">
              <input name="password" type={showPassword ? "text" : "password"} placeholder="Пароль" onChange={onChange} required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 pr-12" />
               <button type="button" className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
              </button>
            </div>
            <button type="submit" className="w-full bg-purple-600 text-white font-semibold py-3 rounded-xl shadow-md hover:bg-purple-700 transition disabled:opacity-50" disabled={isLoggingIn}>
              {isLoggingIn ? "Вход..." : "Войти"}
            </button>
            <button type="button" onClick={doGoogle} className="w-full bg-white border border-gray-300 text-gray-700 py-3 rounded-xl shadow-sm hover:bg-gray-50 transition flex items-center justify-center gap-3 font-medium">
               <GoogleIcon className="w-5 h-5" />
              Войти через Google
            </button>
          </form>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-700">
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-8 relative">
        <div className="flex justify-center mb-6">
          <img src={logo} alt="Лого" className="h-20 w-auto drop-shadow-2xl" />
        </div>
        {renderForm()}
        {msg && <p className={`mt-6 text-center text-sm font-medium ${msg.includes("Ошибка") || msg.includes("❌") ? "text-red-600" : "text-gray-700"}`}>{msg}</p>}
      </div>
    </div>
  );
}
